import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive, ElementRef,
  EmbeddedViewRef, EventEmitter,
  Injector,
  Input, OnDestroy, OnInit, Output, TemplateRef
} from '@angular/core';
import {FirstTimeHighlightComponent} from "./first-time-highlight.component";
import {Utils} from "../../utils/utils";
import {DbResponse} from "../../interceptors/db-response";
import {UserService} from "../../../user/user.service";
import {Store} from "@ngrx/store";
import {DbStoreState} from "../../../store";
import * as fromStore from '../../../store';
import {Subject} from "rxjs";
import {combineLatest} from "rxjs";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";

@Directive({
    // tslint:disable-next-line:directive-selector
    selector: '[firstTimeHighlight]',
    standalone: false
})
export class FirstTimeHighlightDirective implements OnInit, OnDestroy {

  @Input('firstTimeHighlight') content: TemplateRef<any> | string | number;
  @Input() position: 'top'|'bottom'|'left'|'right';
  @Input() align: 'top'|'bottom'|'left'|'right'|'center';
  @Input() size: 'small'|'medium' = 'medium';
  @Input() highlightedElement?: string; // if we want to highlight child element/elements
  @Input() customStyles?: string = ''; // add some classes that does not get with getStylesWithoutDefaults function4
  @Input() hasTooltip?: boolean = true; // show blue tooltip with some position near to highlighted element
  @Input() visible: boolean = true;
  @Input() withShadow: boolean = false;
  @Input() showBtn: boolean = true;
  @Input() animation?: boolean = true;
  @Input() hasCloseBtn?: boolean = true;
  @Input() buttonText?: string = 'Click Me!';
  @Input() localStorageKey?: string;
  @Output() onElementHide: EventEmitter<{clicked: string}>;
  public userLocalStorage: any;

  private _componentRef: ComponentRef<FirstTimeHighlightComponent>;
  private destroyStream: Subject<void> = new Subject();

  constructor(
    private _element: ElementRef,
    private _applicationRef: ApplicationRef,
    private _injector: Injector,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _userService: UserService,
    private _store: Store<DbStoreState>,
  ) {
    this.onElementHide = new EventEmitter<{clicked: string}>();
  }

  ngOnInit() {
    this.getUserInfo();
  }

  public getUserInfo(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUserLocalStorage)
        )),
        first()
      )
      .subscribe(([userLocalStorage]) => {
        this.userLocalStorage = userLocalStorage;
        !this.userLocalStorage[this.localStorageKey] && setTimeout(() => this.showElement(), 2000);
      });
  }

  private showElement(): void {
    if (this._componentRef || !this.content || !this.visible) {
      return;
    }

    // create a Element component reference
    this._componentRef = this._componentFactoryResolver.resolveComponentFactory(FirstTimeHighlightComponent).create(this._injector);
    this._componentRef.instance.host = this._element.nativeElement;
    if (this.content instanceof TemplateRef) {
      this._componentRef.instance.template = this.content;
    } else {
      this._componentRef.instance.content = this.content;
    }
    this._componentRef.instance.position = this.position || 'bottom';
    this._componentRef.instance.align = this.align || 'center';
    this._componentRef.instance.size = this.size || 'medium';
    this._componentRef.instance.showBtn = this.showBtn;
    this._componentRef.instance.highlightedElement = this.highlightedElement;
    this._componentRef.instance.customStyles = this.customStyles;
    this._componentRef.instance.hasTooltip = this.hasTooltip;
    this._componentRef.instance.animation = this.animation;
    this._componentRef.instance.hasCloseBtn = this.hasCloseBtn;
    this._componentRef.instance.buttonText = this.buttonText;
    this._componentRef.instance.withShadow = Utils.isTruthy(this.withShadow) || false;
    this._componentRef.instance.btnEvent.subscribe(event => {
      switch (event) {
        case "clicked":
          this.hideElement();
          break;
      }
    });

    // attach component to the applicationRef so that it will be dirty checked.
    this._applicationRef.attachView(this._componentRef.hostView);

    // get DOM element from component
    const elementComponentElement: HTMLElement = (this._componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(elementComponentElement);
  }

  private hideElement(): void {
    /* after first time btn clicked hide highlighter */
    if (this.localStorageKey && this.userLocalStorage) {
      this.setToLocalStorage();
    }
    this.onElementHide.emit({
      clicked: 'clicked'
    });
    this._componentRef && this._componentRef.destroy();
    this._componentRef = null;
  }

  public setToLocalStorage(): void {
    this.userLocalStorage[this.localStorageKey] = true;
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        filter((_response: DbResponse): boolean => !!_response),
        takeUntil(this.destroyStream),
      first())
      .subscribe((_response: DbResponse): void => {
        if (_response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }

  ngOnDestroy() {
    this.hideElement();
  }

}
